// Boundary helpers for player-preference API <-> editor state.
// Pure functions. No React, no Flux, no Redux. Safe to import from
// either side of the editor migration.

import { serializeProperties } from '@invintusmedia/preference-model-manager';

// unwrap(response) -- API response -> editor inputs.
//
// Reads the Invintus live envelope and returns the row pieces the editor
// needs (prefID, name) plus the inner property bag for normalize() and a
// loadFailed flag for the Save guard rail.
//
// loadFailed=true cases:
//   - data is null (row not found -- e.g. prefID deleted before fetch)
//   - data.playerPref is null/missing (deleted-mid-edit race)
//
// Server-side error envelopes (errors.hasError === true) are caught by
// route helpers upstream (auth0, client security roles, content groups)
// before the editor mounts, so unwrap does not branch on them.
//
// Callers: editor load path. New-pref flow does not call unwrap -- it goes
// straight to freshDefaults().

export function unwrap(response) {
  if (!response) {
    return { inner: null, prefID: undefined, name: '', loadFailed: true };
  }
  const data = response.data;
  if (!data) {
    return { inner: null, prefID: undefined, name: '', loadFailed: true };
  }
  const inner = data.playerPref;
  if (!inner || typeof inner !== 'object') {
    return { inner: null, prefID: data.prefID, name: '', loadFailed: true };
  }
  const name = typeof inner.name === 'string' ? inner.name : '';
  return { inner, prefID: data.prefID, name, loadFailed: false };
}

// wrap({ clientID, prefID, name, properties }) -- editor state -> save body.
//
// Produces the live Invintus wire shape for both add and update:
//   add:    { clientID,         playerPref: { name, ...props } }
//   update: { clientID, prefID, playerPref: { name, ...props } }
//
// prefID is a top-level sibling, NOT inside playerPref. Including prefID
// triggers update on the backend; omitting it triggers add. We omit the
// key entirely on add rather than passing prefID: undefined so the wire
// payload stays clean.
//
// name lives INSIDE playerPref alongside the flattened properties, not
// at top level. This is the v4 contract documented in PMM's editor doc.
//
// serializeProperties strips any incidental editor-state keys -- only
// real PMM property keys survive into the wire body.

export function wrap({ clientID, prefID, name, properties }) {
  return {
    clientID,
    ...(prefID ? { prefID } : {}),
    playerPref: {
      name: typeof name === 'string' ? name : '',
      ...serializeProperties(properties),
    },
  };
}
